import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Sparkles, ChevronDown, FileText, Copy, Check, ShieldQuestion,
} from 'lucide-react'
import toast from 'react-hot-toast'
import DiffView from './DiffView'
import SideBySideDiffView from './SideBySideDiffView'
import { useSettingsStore } from '@/store/settings'
import type { AiSuggestionMessage, SuggestionConfidence } from '@/api/client'

interface Props {
  suggestion: AiSuggestionMessage
}

const CONFIDENCE_LABEL: Record<SuggestionConfidence, string> = {
  HIGH: 'High confidence',
  MEDIUM: 'Medium confidence',
  LOW: 'Low confidence',
  UNKNOWN: 'Unknown confidence',
}

const CONFIDENCE_STYLE: Record<SuggestionConfidence, { color: string; bg: string; border: string }> = {
  HIGH:    { color: 'var(--score-good)',    bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.35)' },
  MEDIUM:  { color: 'var(--score-average)', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.35)' },
  LOW:     { color: 'var(--score-poor)',    bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.35)' },
  UNKNOWN: { color: 'var(--text-muted)',    bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.10)' },
}

export default function SuggestionCard({ suggestion }: Props) {
  const [open, setOpen] = useState(false)
  const [copiedAll, setCopiedAll] = useState(false)

  const confidence = suggestion.confidence ?? 'UNKNOWN'
  const cStyle = CONFIDENCE_STYLE[confidence]
  const edits = suggestion.edits ?? []

  const copyAllPatches = async () => {
    const bundle = edits
      .map(e => e.patch?.trim() ?? '')
      .filter(p => p.length > 0)
      .join('\n')
    if (!bundle) return
    try {
      await navigator.clipboard.writeText(bundle)
      setCopiedAll(true)
      toast.success(`Copied ${edits.length} patch${edits.length === 1 ? '' : 'es'} to clipboard.`)
      setTimeout(() => setCopiedAll(false), 1800)
    } catch {
      toast.error('Could not copy to clipboard.')
    }
  }

  return (
    <div
      className="glass rounded-xl overflow-hidden"
      style={{ border: '1px solid var(--border-subtle)' }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start gap-3 px-5 py-4 text-left transition-colors"
        style={{ background: open ? 'rgba(99,102,241,0.05)' : 'transparent' }}
        aria-expanded={open}
      >
        <Sparkles size={14} className="mt-0.5 shrink-0 text-accent" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{suggestion.auditTitle ?? suggestion.auditCheckId}</span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
              style={{ color: cStyle.color, background: cStyle.bg, border: `1px solid ${cStyle.border}` }}
            >
              {confidence === 'UNKNOWN' && <ShieldQuestion size={9} />}
              {CONFIDENCE_LABEL[confidence]}
            </span>
            {edits.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10"
                style={{ color: 'var(--text-muted)' }}>
                {edits.length} edit{edits.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {suggestion.summary && (
            <p className="text-xs leading-relaxed mt-1.5" style={{ color: 'var(--text-secondary)' }}>
              {suggestion.summary}
            </p>
          )}
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}
          className="shrink-0 mt-0.5" style={{ color: 'var(--text-muted)' }}>
          <ChevronDown size={15} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-5 pb-5 pt-1" style={{ borderTop: '1px solid var(--border-subtle)' }}>
              {edits.length === 0 ? (
                <p className="text-xs pt-3" style={{ color: 'var(--text-muted)' }}>
                  No concrete edits were generated. {suggestion.summary
                    ? 'See the summary above for what additional context might help.'
                    : ''}
                </p>
              ) : (
                <>
                  <div className="flex justify-end pt-3 pb-2">
                    <button
                      type="button"
                      onClick={copyAllPatches}
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[var(--text-secondary)] hover:border-accent/40 hover:text-accent transition-colors"
                    >
                      {copiedAll ? <Check size={11} /> : <Copy size={11} />}
                      {copiedAll ? 'Copied' : `Copy all ${edits.length === 1 ? 'patch' : 'patches'}`}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {edits.map((edit, i) => (
                      <EditBlock key={i} index={i} edit={edit} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function EditBlock({ edit, index }: { edit: AiSuggestionMessage['edits'][number]; index: number }) {
  const [copied, setCopied] = useState(false)
  const diffView = useSettingsStore(s => s.diffView)

  const copyPatch = async () => {
    if (!edit.patch) return
    try {
      await navigator.clipboard.writeText(edit.patch)
      setCopied(true)
      toast.success('Patch copied to clipboard.')
      setTimeout(() => setCopied(false), 1800)
    } catch {
      toast.error('Could not copy to clipboard.')
    }
  }

  return (
    <div className="rounded-md border border-white/5">
      <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.02]"
        style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <FileText size={12} className="shrink-0 text-[var(--text-muted)]" />
        <code className="flex-1 text-[11px] font-mono truncate" style={{ color: 'var(--text-primary)' }}>
          {edit.file || `edit #${index + 1}`}
        </code>
        <button
          type="button"
          onClick={copyPatch}
          className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--text-secondary)] hover:border-accent/40 hover:text-accent transition-colors"
        >
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'Copied' : 'Copy patch'}
        </button>
      </div>
      {edit.explanation && (
        <p className="text-xs leading-relaxed px-3 py-2" style={{ color: 'var(--text-secondary)' }}>
          {edit.explanation}
        </p>
      )}
      {edit.patch && (
        diffView === 'side-by-side'
          ? <SideBySideDiffView patch={edit.patch} className="m-3 mt-0" />
          : <DiffView patch={edit.patch} className="m-3 mt-0" />
      )}
    </div>
  )
}
